/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Primary action primitive with 7 visual variants and 4 sizes
 *   PROPS:   variant (primary|secondary|trading-up|trading-down|ghost|danger|text)
 *            size (sm|md|lg|subscribe)
 *   STATE:   none
 *   DEPENDS: M-CN (cn utility)
 *   EMITS:   native button events via spread props
 *   ACCESSIBILITY: focus-visible ring (info), min 44px touch target (md/lg), keyboard operable
 *   LINKS:   M-BTN
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-07-03] CLAWE-158: Removed primary-pill variant (unused). All CTAs now use primary
 *                variant (rounded-md 6px) for consistent button shape across the app.
 *   [2026-06-12] Added GRACE MODULE_CONTRACT and semantic blocks — no behavior change
 */

import { forwardRef } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/cn"

// --- VariantDefinitions ---
// CLAWE-1132: heights are mobile-first — every interactive element is a 44px
// touch target on phones (`h-11`), and the previous compact heights return from
// `md:` up so desktop density is untouched.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-body font-semibold text-sm transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/50 active:scale-[0.98] disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "bg-primary text-on-primary hover:bg-primary-active rounded-md h-11 px-6 md:h-10",
        secondary:
          "bg-surface-card-dark text-on-dark hover:bg-surface-elevated-dark rounded-md h-11 px-5 md:h-10",
        "trading-up":
          "bg-trading-up text-white hover:bg-trading-up/90 rounded-sm h-11 px-5 md:h-8",
        "trading-down":
          "bg-trading-down text-white hover:bg-trading-down/90 rounded-sm h-11 px-5 md:h-8",
        ghost: "text-muted hover:text-on-dark hover:bg-surface-card-dark rounded-md h-11 px-4 md:h-10",
        danger: "bg-trading-down text-white hover:bg-trading-down/90 rounded-md h-11 px-6 md:h-10",
        text: "text-primary hover:text-primary-active rounded-md h-11 px-3 md:h-9",
      },
      size: {
        sm: "h-11 px-4 text-xs md:h-8",
        md: "",
        lg: "h-12 px-8 text-base",
        subscribe: "h-11 px-4 text-xs md:h-7",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
)

// --- TypeDefinitions ---
interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

// --- ButtonComponent ---
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
export type { ButtonProps }
